<template>
  <v-app class="login-app">
    <v-container class="fill-height">
      <v-row
        justify="center"
        align="center"
      >
        <v-col
          cols="12"
          sm="9"
          md="6"
          lg="5"
          class="login-column"
        >
          <v-card
            class="login-card"
            :elevation="0"
          >
            <!-- Header -->
            <div class="login-header">
              <v-img
                :src="brand.logo"
                width="176"
                class="login-logo"
              />
              <h1 class="login-title">
                {{ brand.productName }}
              </h1>
              <p class="login-subtitle">
                Access support, resources, and account tools.
              </p>
            </div>

            <!-- Login form -->
            <div class="login-content">
              <v-form @submit.prevent="handleContinue">
                <v-text-field
                  v-model="email"
                  label="Email address"
                  type="email"
                  variant="outlined"
                  density="comfortable"
                  autocomplete="email"
                  placeholder="Email address"
                  :error-messages="emailError"
                  class="login-email mb-4"
                />

                <v-btn
                  block
                  color="primary"
                  size="large"
                  :loading="loginLoading"
                  class="login-btn-primary"
                  type="submit"
                >
                  <v-icon start>
                    mdi-login
                  </v-icon>
                  Continue to sign in
                </v-btn>
              </v-form>

              <p class="employee-hint">
                Continue to your secure sign-in.
              </p>

              <div class="login-divider-row">
                <span class="login-divider-line" />
                <span class="login-divider-label">New to {{ brand.brandName }}?</span>
                <span class="login-divider-line" />
              </div>

              <v-btn
                block
                color="primary"
                variant="outlined"
                size="large"
                :loading="registerLoading"
                class="login-btn-secondary"
                @click="handleRegister"
              >
                <v-icon start>
                  mdi-account-plus
                </v-icon>
                Create Account
              </v-btn>
            </div>

            <!-- Footer -->
            <div class="login-footer">
              By signing in you agree to our
              <span class="login-footer-terms">Terms of Service</span>
              and
              <span class="login-footer-terms">Privacy Policy.</span>
              Need help?
              <a
                :href="`mailto:${brand.supportEmail}`"
                class="login-footer-link"
              >{{ brand.supportEmail }}</a>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import { brand } from '@/config/brand'
import { useAuthStore } from '@/stores/auth'
import { EMPLOYEE_EMAIL_DOMAINS } from '@/stores/auth/auth.constants'

const authStore = useAuthStore()
const email = ref('')
const emailTouched = ref(false)
const loginLoading = ref(false)
const registerLoading = ref(false)

const emailError = computed(() => {
  if (!emailTouched.value) {
    return ''
  }

  if (!email.value.trim()) {
    return 'Email is required'
  }

  // Basic format check; backend still validates on callback exchange.
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())
  if (!isValidEmail) {
    return 'Enter a valid email'
  }

  return ''
})

const isEmployeeEmail = (inputEmail: string): boolean => {
  const domain = inputEmail.trim().toLowerCase().split('@')[1]
  if (!domain) {
    return false
  }

  return EMPLOYEE_EMAIL_DOMAINS.includes(domain)
}

const handleContinue = async () => {
  emailTouched.value = true

  if (emailError.value) {
    return
  }

  loginLoading.value = true
  const normalizedEmail = email.value.trim().toLowerCase()
  try {
    if (isEmployeeEmail(normalizedEmail)) {
      await authStore.authorizeMicrosoft()
      return
    }

    await authStore.authorize({ email: normalizedEmail })
  } finally {
    loginLoading.value = false
  }
}

const handleRegister = async () => {
  registerLoading.value = true
  await authStore.authorize({ screen_hint: 'signup' })
}
</script>

<style scoped>
/* ── Page ────────────────────────────────────────────── */
.login-app :deep(.v-application__wrap) {
  /* Calm cool gradient — card reads as foreground at any screen size */
  background: linear-gradient(150deg, #eef4fb 0%, #e5eef6 55%, #dde8f2 100%);
}

/* ── Column ──────────────────────────────────────────── */
.login-column {
  max-width: 420px !important;
}

/* ── Card ────────────────────────────────────────────── */
.login-card {
  border-radius: 16px;
  overflow: hidden;
  background: #ffffff;
  /*
   * Four-layer shadow:
   *   1. 0 0 0 1px  — replaces a hard border with a feathered ring
   *   2. 0 1px 3px  — tight contact shadow
   *   3. 0 6px 16px — mid-field depth
   *   4. 0 20px 40px — ambient lift
   */
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.05),
    0 1px 3px rgba(0, 0, 0, 0.04),
    0 6px 16px rgba(0, 0, 0, 0.07),
    0 20px 40px rgba(0, 0, 0, 0.06) !important;
}

/* ── Header ──────────────────────────────────────────── */
.login-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 40px 28px;
  text-align: center;
}

.login-logo {
  margin-bottom: 16px;
}

.login-title {
  font-size: 1.125rem;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: rgba(0, 0, 0, 0.85);
  margin-bottom: 6px;
}

.login-subtitle {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.58);
  margin: 0;
  line-height: 1.5;
  letter-spacing: 0.005em;
}

/* ── Tab content ─────────────────────────────────────── */
.login-content {
  padding: 8px 40px 36px;
}

/* ── Input polish ─────────────────────────────────────── */
:deep(.login-email .v-field.v-field--variant-outlined .v-field__outline) {
  color: rgba(0, 0, 0, 0.42);
}

:deep(.login-email .v-field.v-field--focused .v-field__outline) {
  color: #0077BE;
}

:deep(.login-email .v-field.v-field--focused) {
  box-shadow: 0 0 0 3px rgba(0, 119, 190, 0.10);
  border-radius: 8px;
}

/* ── Primary button ──────────────────────────────────── */
.login-btn-primary {
  border-radius: 8px !important;
  font-weight: 600 !important;
  letter-spacing: 0.06em;
  background-color: #006EAF !important;
  /*
   * Coloured shadow — the hue matches the button fill so the
   * shadow reads as a glow rather than a generic drop shadow.
   */
  box-shadow:
    0 1px 2px rgba(0, 119, 190, 0.16),
    0 4px 12px rgba(0, 119, 190, 0.22) !important;
  transition:
    box-shadow 160ms ease,
    filter 160ms ease !important;
}

.login-btn-primary:hover {
  filter: brightness(1.03);
  box-shadow:
    0 2px 6px rgba(0, 98, 160, 0.24),
    0 8px 20px rgba(0, 98, 160, 0.30) !important;
}

.login-btn-primary:active {
  filter: brightness(0.97);
  box-shadow:
    0 1px 2px rgba(0, 119, 190, 0.14),
    0 2px 6px rgba(0, 119, 190, 0.18) !important;
}

/* ── Secondary button ────────────────────────────────── */
.login-btn-secondary {
  border-radius: 8px !important;
  letter-spacing: 0.06em;
  /*
   * Soften the border: Vuetify's outlined uses the full primary
   * color which competes with the primary CTA. A 50% alpha version
   * keeps it clearly secondary while still intentional.
   */
  border-color: rgba(0, 119, 190, 0.45) !important;
  transition: border-color 160ms ease, box-shadow 160ms ease !important;
}

.login-btn-secondary:hover {
  border-color: rgba(0, 119, 190, 0.75) !important;
}

/* ── Divider row ─────────────────────────────────────── */
.login-divider-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 24px 0;
}

.login-divider-line {
  flex: 1;
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
}

.login-divider-label {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.30);
  white-space: nowrap;
}

/* ── Employee hint ───────────────────────────────────── */
.employee-hint {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.52);
  text-align: center;
  margin: 14px 0 22px;
  line-height: 1.6;
}

/* ── Footer ──────────────────────────────────────────── */
.login-footer {
  background: transparent;
  border-top: 1px solid rgba(0, 0, 0, 0.045);
  padding: 14px 40px;
  font-size: 0.6875rem;
  color: rgba(0, 0, 0, 0.40);
  text-align: center;
  line-height: 1.7;
}

/* "Terms of Service" and "Privacy Policy" — readable but not a CTA */
.login-footer-terms {
  color: rgba(0, 0, 0, 0.46);
}

.login-footer-link {
  color: rgba(0, 119, 190, 0.86);
  text-decoration: none;
  transition: color 160ms ease;
}

.login-footer-link:hover {
  color: #0077BE;
}

/* ── Mobile ──────────────────────────────────────────── */
@media (max-width: 600px) {
  .login-header {
    padding: 36px 24px 24px;
  }

  .login-content {
    padding: 8px 24px 32px;
  }

  .login-footer {
    padding: 14px 24px;
  }
}
</style>
